import type { AIMetric, AIPlan } from '../api/dataApi';
import type { JsonValue, MappingOutputSpec, MappingRule, MappingSpec } from './mappingEngine';
import { previewMapping } from './mappingPreview';

// Turns a (server-validated) AI plan into a runnable webhook MappingSpec.
export function planToSpec(plan: AIPlan, channel: string, bodyFormat: string | undefined, recordPath: string | undefined): MappingSpec {
  const rules: MappingRule[] = plan.rules.map((r) => {
    const out: MappingRule = { to: r.to };
    if (r.from) out.from = r.from;
    else if (r.const !== undefined) out.const = r.const;
    if (r.cast) out.cast = r.cast;
    if (r.unitScale !== undefined) out.unitScale = r.unitScale;
    if (r.timestamp) out.timestamp = r.timestamp;
    return out;
  });
  const path = plan.recordPath || recordPath;
  const calc = planCalc(plan, rules);
  return { channel, provider: 'generic', ...(bodyFormat ? { format: bodyFormat } : {}), ...(path ? { recordPath: path } : {}), rules, ...(calc ? { calc } : {}) };
}

// The Output Value calculator's block (backend internal/outvalue): how the
// value is shown and which fields hold the period it measures. Paths are
// relative to one record; the value is whatever the plan's `value` rule reads.
// The rules (and so the legacy value) are left exactly as planned.
function planCalc(plan: AIPlan, rules: MappingRule[]): Record<string, unknown> | undefined {
  const c = plan.calc;
  const from = rules.find((r) => r.to === 'value')?.from;
  if (!c || !from) return undefined;
  return {
    value: { field: from, type: c.type, ...(c.unit ? { unit: c.unit } : {}), ...(c.unitPath ? { unitPath: c.unitPath } : {}) },
    ...(c.period?.endPath ? { period: { ...(c.period.startPath ? { startPath: c.period.startPath } : {}), endPath: c.period.endPath, ...(c.period.framePath ? { framePath: c.period.framePath } : {}) } } : {}),
  };
}

// The safety net after the AI: the plan must actually produce Signals from the
// user's own example. Returns how many of the first records yielded one.
export function countProducedSignals(spec: MappingSpec, records: JsonValue[]): { produced: number; tried: number } {
  const sample = records.slice(0, 20);
  const results = previewMapping(spec, sample, 'signal');
  const produced = results.filter((r) => r.outputs.some((o) => o.signal && o.signal.value.trim() !== '' && Number.isFinite(Number(o.signal.value)))).length;
  return { produced, tried: sample.length };
}

// True when the pasted text reads as a description of a data source - a spec or
// README (https URL + markdown structure), just URL(s), or a sentence around a
// URL - rather than sample records.
export function looksLikeDescription(text: string): boolean {
  const t = text.trim();
  if (!/https?:\/\//i.test(t)) return false;
  if (t.startsWith('{') || t.startsWith('[') || t.startsWith('<')) return false;
  const lines = t.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.every((l) => /^https?:\/\/\S+$/i.test(l))) return true; // only URLs
  if (/^#{1,6}\s|^\s*[-*]\s|^```/m.test(t)) return true;
  // prose around a URL ("track https://... for Apple"), not a table that holds a URL column
  const prose = t.replace(/https?:\/\/\S+/gi, ' ');
  return !/\t/.test(t) && !/,.*,/.test(t) && (prose.match(/[A-Za-z]{2,}/g) ?? []).length >= 3;
}

// With several sources of one kind, each mapping tags its Signals with the
// entity it follows (unless the plan already labels one itself).
export function withEntityLabel(spec: MappingSpec, label: string): MappingSpec {
  if (!label) return spec;
  const has = (rules: MappingRule[]) => rules.some((r) => r.to === 'labels.entity');
  const add = (rules: MappingRule[]) => (has(rules) ? rules : [...rules, { to: 'labels.entity', const: label }]);
  if (Array.isArray(spec.outputs)) {
    if (spec.outputs.every((o) => has(o.rules ?? []))) return spec;
    return { ...spec, outputs: spec.outputs.map((o) => ({ ...o, rules: add(o.rules ?? []) })) };
  }
  if (has(spec.rules ?? [])) return spec;
  return { ...spec, rules: add(spec.rules ?? []) };
}

// ---- one document, many metrics --------------------------------------------
// The planner made ONE plan for ONE record list; every sibling list the server
// found with the same shape is a metric of its own, read by the same rules. The
// poller does one fetch and applies each output to its own list.

export const METRICS_PER_INTEGRATION = 250; // outputs per mapping - the spec stays a sane size
export const METRIC_LAST_RECORDS = 6; // newest records of each list per poll
export const TEMPLATE_CHUNK = 100; // Signals per template

export function metricRules(rules: MappingRule[], m: AIMetric): MappingRule[] {
  return [
    ...rules.filter((r) => r.to !== 'name' && r.to !== 'labels.unit'),
    { to: 'name', const: m.name },
    ...(m.unit ? [{ to: 'labels.unit', const: m.unit }] : []),
  ];
}

// The calc block fits a metric only when its unit matches: a currency block is
// re-pointed at another ISO currency, and dropped for anything else (shares,
// ratios...) rather than showing "$" for something that is not money.
export function metricCalc(calc: Record<string, unknown> | undefined, m: AIMetric): Record<string, unknown> | undefined {
  const value = calc?.value as { type?: string; unit?: string } | undefined;
  if (!calc || !value) return undefined;
  if (!m.unit || m.unit === value.unit) return calc;
  if (value.type === 'currency' && /^[A-Z]{3}$/.test(m.unit)) return { ...calc, value: { ...value, unit: m.unit } };
  return undefined;
}

// One metric as an ordinary single-list mapping - what the review previews.
export function metricPreviewSpec(spec: MappingSpec, m: AIMetric): MappingSpec {
  return { ...spec, recordPath: m.path, rules: metricRules(spec.rules ?? [], m) };
}

// The mappings that read all the metrics: outputs[], each with its own list,
// name, unit label and calc, split so one mapping never holds an unbounded spec.
export function metricsSpecs(spec: MappingSpec, metrics: AIMetric[]): MappingSpec[] {
  const { rules, calc, recordPath: _rp, ...rest } = spec;
  void _rp;
  const out: MappingSpec[] = [];
  for (let i = 0; i < metrics.length; i += METRICS_PER_INTEGRATION) {
    const outputs: MappingOutputSpec[] = metrics.slice(i, i + METRICS_PER_INTEGRATION).map((m) => {
      const c = metricCalc(calc as Record<string, unknown> | undefined, m);
      return { recordPath: m.path, last: METRIC_LAST_RECORDS, rules: metricRules(rules ?? [], m), ...(c ? { calc: c } : {}) };
    });
    out.push({ ...rest, outputs });
  }
  return out;
}

// Adds an output per metric to an existing mapping (the editor's "add all
// metrics"), using its first output as the template: same rules with the name
// swapped for the metric's, same calc re-pointed at the metric's unit. Lists the
// mapping already reads are skipped. The mapping-level calc moves onto output 1,
// since once outputs[] exists only per-output calc is read.
export function addMetricOutputs(spec: MappingSpec, metrics: AIMetric[]): MappingSpec {
  const explicit: MappingSpec = Array.isArray(spec.outputs) ? spec : (() => { const { rules, ...rest } = spec; return { ...rest, outputs: [{ rules: Array.isArray(rules) ? rules : [] }] }; })();
  const outputs = [...(explicit.outputs ?? [])];
  const { calc: specCalc, ...rest } = explicit;
  const base = outputs[0] ?? { rules: [] };
  const baseCalc = (base.calc ?? specCalc) as Record<string, unknown> | undefined;
  if (baseCalc && !base.calc) outputs[0] = { ...base, calc: baseCalc };
  const have = new Set(outputs.map((o) => o.recordPath ?? (typeof spec.recordPath === 'string' ? spec.recordPath : '')));
  for (const m of metrics) {
    if (have.has(m.path)) continue;
    const c = metricCalc(baseCalc, m);
    outputs.push({ recordPath: m.path, last: METRIC_LAST_RECORDS, rules: metricRules(base.rules ?? [], m), ...(c ? { calc: c } : {}) });
  }
  return { ...(specCalc && !baseCalc ? { ...rest, calc: specCalc } : rest), outputs };
}
